import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const emptyProduct = {
  title: '',
  description: '',
  icon: 'box',
  features: [],
  price: '',
  order: 0,
  active: true,
};

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [featuresText, setFeaturesText] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await adminApi.getProducts();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFeaturesText(product.features?.join('\n') || '');
    } else {
      setEditingProduct({ ...emptyProduct, order: products.length });
      setFeaturesText('');
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setFeaturesText('');
  };

  const handleChange = (e) => {
    setEditingProduct({ ...editingProduct, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const productData = {
        ...editingProduct,
        features: featuresText.split('\n').filter(f => f.trim()),
      };

      if (editingProduct.id) {
        await adminApi.updateProduct(editingProduct.id, productData);
        toast.success('Product updated successfully');
      } else {
        await adminApi.createProduct(productData);
        toast.success('Product created successfully');
      }
      fetchProducts();
      closeDialog();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await adminApi.deleteProduct(id);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="products-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Products</h1>
          <p className="text-muted-foreground">Manage your product offerings.</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Price</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} data-testid={`product-row-${product.id}`}>
                <td className="font-medium">{product.title}</td>
                <td className="text-primary">{product.price || '-'}</td>
                <td>
                  <span className={product.active ? 'badge-success' : 'badge-error'}>
                    {product.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(product)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(product.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted-foreground py-8">
                  No products yet. Add your first product.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingProduct?.id ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Title</label>
              <Input
                name="title"
                value={editingProduct?.title || ''}
                onChange={handleChange}
                placeholder="SaaS Starter Kit"
                className="input-neon"
                data-testid="product-title-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <Textarea
                name="description"
                value={editingProduct?.description || ''}
                onChange={handleChange}
                placeholder="Complete starter template for SaaS applications"
                rows={3}
                className="input-neon resize-none"
                data-testid="product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Price</label>
                <Input
                  name="price"
                  value={editingProduct?.price || ''}
                  onChange={handleChange}
                  placeholder="$499"
                  className="input-neon"
                  data-testid="product-price-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Icon Name</label>
                <Input
                  name="icon"
                  value={editingProduct?.icon || ''}
                  onChange={handleChange}
                  placeholder="rocket"
                  className="input-neon"
                  data-testid="product-icon-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Features (one per line)</label>
              <Textarea
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder="Auth System&#10;Billing Integration&#10;Dashboard UI"
                rows={4}
                className="input-neon resize-none"
                data-testid="product-features-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Active</label>
              <Switch
                checked={editingProduct?.active ?? true}
                onCheckedChange={(checked) => setEditingProduct({ ...editingProduct, active: checked })}
                data-testid="product-active-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="ghost" onClick={closeDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-product-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsManager;
